/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SocuDataModelImpl } from '../../core/socu/impls';
import { VideoManager } from '../../core/videoplayer';
import { RootState } from '../../store/root.reducers';
import { Log, VuLogger, QoEAppLogger } from '../../core/logging';
import {
    PlaybackErrorType,
    PlaybackRestrictionType,
    VideoPlayerStates,
} from '../../core/videoplayer/enums';
import { hideSpinner, showSpinner } from '../../store/spinner/spinner.reducers';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { SpinnerDelays } from '../../core/enums/SpinnerDelays';
import { setVideoPlayerState } from '../../store/videoplayer/videoplayerstate/videoplayerstate.reducers';
import { IPlaybackError, IPlayerStatus } from '../../core/videoplayer/interfaces';
import {
    resetPlaybackParamsVideoplayer,
    resetStillThereTimeout,
    toggleIsPlaying,
} from '../../store/videoplayer/videoplayer.reducers';
import { NetworkManager } from '../../core/network/NetworkManager';
import { VideoPlayerUi } from '../VideoPlayerUI';
import { PlaybackErrorDisplay } from '../../core/videoplayer/error';
import { DeviceModel } from '../../core/device';

const FullScreenPlayback: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'FullScreenPlayback';
    const dispatch: Function = useDispatch();

    // List of selectors
    const { channel } = useSelector((state: RootState) => state.videoPlayer);

    // List of local states
    const [cleanupNeeded, setCleanupNeeded] = useState(false);

    const shouldShowLiveTVInitiationMessage = useRef(true);

    PlaybackErrorDisplay.getInstance(dispatch);

    const handlePlaybackError = (error: IPlaybackError | undefined) => {
        if (error === undefined) return;
        Log.d(TAG, 'handlePlaybackError error is', error);
        const { type, data } = error;
        switch (type) {
            case PlaybackErrorType.ILiveTvErrorBase:
                break;

            case PlaybackErrorType.LiveTvErrorBase:
                break;

            case PlaybackErrorType.LiveTvLocationError:
                PlaybackErrorDisplay.getInstance().showVisualDisplay(data);
                break;

            case PlaybackErrorType.LiveTvPlaybackError:
                PlaybackErrorDisplay.getInstance().showVisualDisplay(data);
                break;

            case PlaybackErrorType.LiveTvSessionError:
                PlaybackErrorDisplay.getInstance().showVisualDisplay(data);
                break;

            default:
        }
    };

    const handlePlaybackRestriction = (type: PlaybackRestrictionType) => {
        // TODO: Will be able to handle parental control usecase in future.
        Log.d(TAG, 'handlePlaybackRestriction type is', type);
        switch (type) {
            case PlaybackRestrictionType.Adult:
                Diagnostics.info(TAG, 'Adult Content');
                Log.i(TAG, 'Adult Content');
                QoEAppLogger.logAdultContent();
                VuLogger.logNotifyEvent(
                    VuLogger.NotifyEventSourceType.ADULT_OSD,
                    VuLogger.NotifyEventSourceName.ADULT_SHOW,
                );
                break;

            case PlaybackRestrictionType.Unsubscribed:
                VuLogger.logNotifyEvent(
                    VuLogger.NotifyEventSourceType.UNSUBSCRIBED_OSD,
                    VuLogger.NotifyEventSourceName.UNSUBSCRIBED_CHANNEL,
                );
                break;

            default:
        }
    };

    const handleStreamRestrictionChange = (status: boolean) => {
        Log.d(TAG, 'handleStreamRestrictionChange status is', status);
        PlaybackErrorDisplay.getInstance(dispatch).setIsStreamingRestricted(status);
    };

    const handleVideoStateChange = (status: IPlayerStatus): void => {
        Log.d(TAG, 'handleVideoStateChange status is', status);

        switch (status.state) {
            case VideoPlayerStates.BUFFERING:
                dispatch(
                    showSpinner(
                        Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                        SpinnerDelays.LIVE_TV,
                    ),
                );
                break;

            case VideoPlayerStates.ERROR_RECOVERY:
                dispatch(
                    showSpinner(
                        Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                        SpinnerDelays.LIVE_TV,
                        status.code,
                    ),
                );
                break;

            case VideoPlayerStates.FATAL_ERROR:
                setCleanupNeeded(true);
                handlePlaybackError(status.error);
                dispatch(hideSpinner());
                break;

            case VideoPlayerStates.FIRST_FRAME:
                dispatch(hideSpinner());

                break;

            case VideoPlayerStates.INITIATION:
                if (
                    shouldShowLiveTVInitiationMessage.current &&
                    !SocuDataModelImpl.getInstance().isSocu()
                ) {
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_GOING_TO_LIVE_TV),
                            SpinnerDelays.LIVE_TV,
                        ),
                    );
                    shouldShowLiveTVInitiationMessage.current = false;
                } else if (SocuDataModelImpl.getInstance().isSocu()) {
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_GETTING_YOUR_SHOW),
                            SpinnerDelays.LIVE_TV,
                        ),
                    );
                    shouldShowLiveTVInitiationMessage.current = true;
                } else
                    dispatch(
                        showSpinner(
                            Locale.getString(LocaleKeys.LIVE_TV_SPINNER_LOADING_VIDEO),
                            SpinnerDelays.LIVE_TV,
                        ),
                    );
                break;

            case VideoPlayerStates.LOADED:
                dispatch(setVideoPlayerState(VideoPlayerStates.LOADED));
                VideoManager.getInstance().setCurrentPlayPosition();
                break;

            case VideoPlayerStates.LOADING:
                dispatch(setVideoPlayerState(VideoPlayerStates.LOADING));
                break;

            case VideoPlayerStates.PLAYING:
                dispatch(hideSpinner());
                break;

            case VideoPlayerStates.PAUSED_THRESHOLD:
                VideoManager.getInstance().unPause();
                dispatch(toggleIsPlaying());
                break;

            case VideoPlayerStates.RATE_CHANGE:
                dispatch(hideSpinner());
                break;

            case VideoPlayerStates.UNLOADED:
                dispatch(setVideoPlayerState(VideoPlayerStates.UNLOADED));
                break;

            default:
                Log.d(TAG, 'The state of the video player is unknown.');
        }
    };

    useEffect(() => {
        const refPlaybackErrorHandler =
            VideoManager.getInstance().addPlaybackErrorListener(handlePlaybackError);
        const refPlaybackRestrictionHandler =
            VideoManager.getInstance().addPlaybackRestrictionListener(handlePlaybackRestriction);
        const refRestrictionHandler = VideoManager.getInstance().addStreamRestrictionChangeListener(
            handleStreamRestrictionChange,
        );
        const refVidStateHandler =
            VideoManager.getInstance().addVideoPlayerStateChangeListener(handleVideoStateChange);
        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
            VideoManager.getInstance().mount();
        }
        VideoManager.getInstance().maximize();
        return () => {
            VideoManager.getInstance().removePlaybackErrorListener(refPlaybackErrorHandler);
            VideoManager.getInstance().removePlaybackRestrictionListner(
                refPlaybackRestrictionHandler,
            );
            VideoManager.getInstance().removeStreamRestrictionChangeListener(refRestrictionHandler);
            VideoManager.getInstance().removeVideoPlayerStateChangeListener(refVidStateHandler);
            VideoManager.getInstance().minimize();
            if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                VideoManager.getInstance().unmount();
            }
        };
    }, []);

    useEffect(() => {
        if (cleanupNeeded) {
            VideoManager.getInstance().clean();
        }
    }, [cleanupNeeded]);

    /**
     * The useEffect to handle the playback on channel change.
     */
    useEffect(() => {
        if (NetworkManager.isNetworkDisconnected()) return;
        dispatch(resetStillThereTimeout(true));
        // TODO: Commenting as it is retuning video window.Need to check
        // VideoManager.getInstance().stop();
        dispatch(resetPlaybackParamsVideoplayer());
        /**
        Removed startPlayback() because it was causing chaos:
        VideoManager's playable and socuDataModelImpl were like two people arguing in diff languages
        Also, switching "to love from socu" turned into a melodrama. Problem solved (hopefully).
         */
    }, [channel]);

    return (
        <React.Fragment>
            <VideoPlayerUi />
        </React.Fragment>
    );
};

export default FullScreenPlayback;
